// Import react elements
import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';

// Import navigation hook from React Router
import { useNavigate } from "react-router-dom";

// Import context
import { UserContext } from "global/context/UserContext";

// Import hooks
import {
    handleDragStart,
    handleDrop,
    toggleRowExpansion,
    toggleRowSelection,
    toggleSelectAllRows,
    handleResize
} from 'global/hooks/useTableHandlers';

// Import components
import Pagination from 'global/components/Pagination';
import LoadingDotsAnimation from "global/components/loading_ui/LoadingDotsAnimation"
import ContextMenuButton from 'global/components/ContextMenuButton';
import ToggleSwitch from './ToggleSwitch';

// Import utilities
import { CheckNullDate } from "global/utilities/CheckNullDate";
import { formatDisplayDateTime } from "global/utilities/ConvertDate";

// Import css style sheet
import styles from "./CustomisableTable.module.css";
import { ArrowDownCollapseIcon, ArrowUpCollapseIcon } from 'global/strich_icons/StrichIcons';



// This hook builds filter options and default selected filters for any table
export const useTableFilters = (rows = [], filterKeys = [], buildDefaultFilters) => {

    // This builds the available filter options from the table rows
    const filterOptions = useMemo(() => {

        // This creates an empty object to store filter options
        const options = {};

        // This safely loops through each row
        rows.forEach((row) => {

            // This loops through each configured filter key
            filterKeys.forEach((key) => {

                // This creates a new Set for the key if it does not exist yet
                if (!options[key]) options[key] = new Set();

                // This skips empty values
                if (row[key] === undefined || row[key] === null || row[key] === "") return;

                // This adds the value to the Set
                options[key].add(row[key]);

            });

        });

        // This converts each Set into a sorted array
        return Object.fromEntries(

            // This maps each filter Set into a normal array
            Object.entries(options).map(([key, set]) => [key, Array.from(set).sort()])

        );

    }, [rows, filterKeys]);


    // This builds the default selected filters from the available options
    const defaultFilters = useMemo(() => {

        // If a custom default filter builder was provided, use it
        if (typeof buildDefaultFilters === "function") {

            // This returns the custom defaults
            return buildDefaultFilters(filterOptions);

        }

        // This selects every available option by default
        return Object.entries(filterOptions).reduce((accumulator, [key, values]) => {

            // This stores all values as selected for this filter key
            accumulator[key] = [...values];

            // This returns the updated accumulator
            return accumulator;

        }, {});

    }, [filterOptions, buildDefaultFilters]);


    // This returns both values needed by the table
    return {
        filterOptions,
        defaultFilters,
    };

};



// Create a button based on values passed in
const CustomisableTable = ({

    tableData,

    initialTableColumns,

    filterKeys,

    filters,

    selectedRows,

    setSelectedRows,

    loadingData,

    noDataAvaiableMessage,

    numberOfDisplayedDataRows,

    setNumberOfDisplayedDataRows,

    // Optional pagination state owned by the calling page
    currentPage: suppliedCurrentPage,

    setCurrentPage: suppliedSetCurrentPage,

    rowsPerPage: suppliedRowsPerPage,

    setRowsPerPage: suppliedSetRowsPerPage,

    // Provide a onClick function
    handleRowSingleClick,

    // OR

    // Provide a link builder function instead
    getRowLink,

    menuOptions,

    activeTogglefunction,

    // Boolean state for showing value inc tax or not
    showTax,

    // Prefix for the table row key
    rowKeyPrefix = "",

}) => {


    //console.log("custom table data: ", tableData);

    // Get the navigate function for programmatic navigation
    const navigate = useNavigate();

    // Get the core countries, including their SVG flags
    const { countries = [] } = useContext(UserContext) || {};

    // Render a country name with the SVG flag supplied by the countries API
    const renderCountryDestination = (countryValue) => {

        // Normalise the supplied country value for matching
        const countryLookup = String(countryValue || "").trim().toLowerCase();

        // Find the country by either its code or name
        const country = countries.find((countryItem) => {

            return String(countryItem?.code || "").trim().toLowerCase() === countryLookup
                || String(countryItem?.name || "").trim().toLowerCase() === countryLookup;

        });

        // Render the country with its flag when one is available
        return (

            <div className={styles.shipping_destination_content}>

                {country?.flag_svg &&

                    <span
                        className={styles.country_flag}
                        title={country.name}
                        dangerouslySetInnerHTML={{ __html: country.flag_svg }}
                    />

                }

                <span>{countryValue}</span>

            </div>

        );

    };



    // Set all the states
    const [columns, setColumns] = useState(initialTableColumns);
    const [columnWidths, setColumnWidths] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [textFilters, setTextFilters] = useState({});
    const [expandedRows, setExpandedRows] = useState([]);

    // Keep internal pagination for tables which do not supply their own state
    const [internalCurrentPage, setInternalCurrentPage] = useState(1);
    const [internalRowsPerPage, setInternalRowsPerPage] = useState(10);

    const [showTextFilter, setShowTextFilter] = useState(false);
    const [tableContainerWidth, setTableContainerWidth] = useState(0);
    const dragColumnIndex = useRef(null);

    // Store a reference to the top of the table area
    const tableTopRef = useRef(null);

    // Use the table's internal pagination by default
    let currentPage = internalCurrentPage;
    let setCurrentPage = setInternalCurrentPage;
    let rowsPerPage = internalRowsPerPage;
    let setRowsPerPage = setInternalRowsPerPage;
    let paginationIsControlled = false;

    // Use caller-owned pagination when the complete controlled state was supplied
    if (
        Number.isInteger(suppliedCurrentPage) &&
        suppliedCurrentPage > 0 &&
        typeof suppliedSetCurrentPage === "function" &&
        Number.isInteger(suppliedRowsPerPage) &&
        suppliedRowsPerPage > 0 &&
        typeof suppliedSetRowsPerPage === "function"
    ) {

        currentPage = suppliedCurrentPage;
        setCurrentPage = suppliedSetCurrentPage;
        rowsPerPage = suppliedRowsPerPage;
        setRowsPerPage = suppliedSetRowsPerPage;
        paginationIsControlled = true;

    }

       
    // Scroll the table back into view.
    const handleScrollToTableTop = () => {

        // Stop if the ref hasn't been created yet.
        if (!tableTopRef.current) return;

        // Wait until React has rendered the new page.
        requestAnimationFrame(() => {

            // Smoothly scroll the table into view.
            tableTopRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });

        });

    };



    // Track the available table container width so flexible columns can be calculated in pixels
    useEffect(() => {

        // This updates the stored container width
        const updateTableContainerWidth = () => {

            // Stop if the ref has not mounted yet
            if (!tableTopRef.current) { return; }

            // Store the current container width
            setTableContainerWidth(tableTopRef.current.clientWidth || 0);

        };

        // Run once immediately
        updateTableContainerWidth();

        // Use ResizeObserver when available
        if (typeof ResizeObserver !== "undefined" && tableTopRef.current) {

            // Create the observer
            const resizeObserver = new ResizeObserver(updateTableContainerWidth);

            // Observe the table container
            resizeObserver.observe(tableTopRef.current);

            // Clean up observer
            return () => resizeObserver.disconnect();

        }

        // Fallback to window resize
        window.addEventListener("resize", updateTableContainerWidth);

        // Clean up window listener
        return () => window.removeEventListener("resize", updateTableContainerWidth);

    }, []);



    // ************************************************************************************************
    // NOTE: This includes all data if there are no filter groups
    // NOTE: It will remove any row where a filter is unselected
    // ************************************************************************************************
    // Memoize the filtered data to avoid recalculating unless tableData, filters, or textFilters change
    const filteredData = useMemo(() => {

        // Check whether the table data is a real array
        if (!Array.isArray(tableData)) {

            return [];

        }

        // Check whether this table has checkbox filters available
        const hasCheckboxFilters = filters && Object.keys(filters).length > 0;

        // Filter the tableData array
        return tableData.filter((row) => {

            // Check if the row matches all selected checkbox filters
            const matchesCheckboxFilters = hasCheckboxFilters
                ? Object.entries(filters).every(([key, values]) => {

                    // If nothing is selected in this filter group, no rows should match
                    if (!Array.isArray(values) || values.length === 0) {

                        return false;

                    }

                    // Otherwise, check if the row's value is included in the selected filter values
                    return values.includes(row[key]);

                })
                : true;

            // Check if the row matches all text-based filters
            const matchesTextFilters = Object.entries(textFilters).every(([key, val]) => {

                // If there is no text entered for this filter, treat it as a match
                if (!val) {

                    return true;

                }

                // Find the column config for this filter
                const column = columns.find((col) => col.key === key);

                // Use the column filter key when provided, otherwise use the column key
                const filterKey = column?.filterKey || key;

                // Otherwise, check if the row's filter value contains the text entered
                return String(row[filterKey] ?? '').toLowerCase().includes(val.toLowerCase());

            });

            // Return true if the row matches both checkbox and text filters
            return matchesCheckboxFilters && matchesTextFilters;

        });

    }, [tableData, filters, textFilters, columns]);


    // Reset internally managed tables when their data changes
    useEffect(() => {

        // Caller-owned pagination should survive the table being unmounted and rebuilt
        if (paginationIsControlled) {
            return;
        }

        // Reset ordinary tables to their first page
        setCurrentPage(1);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData, paginationIsControlled]);



    // TABLE FORMATTING AND SORTING //

    // React effect hook to calculate and set fixed column widths when columns change
    useEffect(() => {

        // Update widths without forcing flexible columns to fixed widths
        setColumnWidths((previousWidths) => {

            // Initialise an object to store computed widths
            const widths = {};

            // Iterate through each column configuration provided
            columns.forEach((col) => {

                // Fixed columns always get their configured width
                if (col.resizable === false) {

                    widths[col.key] = col.minWidth || col.maxWidth || 150;

                    return;

                }

                // Flexible columns only get a stored width after manual resize
                if (previousWidths[col.key]) {

                    widths[col.key] = previousWidths[col.key];

                }

            });

            // Return the updated widths
            return widths;

        });

    }, [columns]);



    // Create a shallow copy of 'filteredData' and sort it based on the current 'sortConfig'
    const sortedData = [...(Array.isArray(filteredData) ? filteredData : [])].sort((a, b) => {

        // If no sorting key is specified, maintain original order
        if (!sortConfig.key) return 0;

        // Find the column config for this sort
        const sortColumn = columns.find((col) => col.key === sortConfig.key);

        // Use the column sort key when provided, otherwise use the column key
        const sortKey = sortColumn?.sortKey || sortConfig.key;

        // Retrieve values to compare from each item using the sort key
        const aValue = a[sortKey];
        const bValue = b[sortKey];

        // If aValue is less than bValue, return -1 for ascending or 1 for descending
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;

        // If aValue is greater than bValue, return 1 for ascending or -1 for descending
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;

        // Values are equal; preserve original sequence
        return 0;

    });



    // Update total number of table rows whenever the tableData or sortedData (checkbox filters) changes
    useEffect(() => {

        // Use the length of the currently active dataset
        // Check if the table data has been filtered (sorted will be less than the total)
        const isFiltered = tableData?.length !== sortedData.length;

        // If it has been filtered then set the current sorted row count else set it to the length of tableData
        const currentRowCount = isFiltered ? sortedData.length : tableData.length;

        // Update the total number of displayed rows count if setNumberOfDisplayedDataRows is a function
        if (typeof setNumberOfDisplayedDataRows === 'function') {

            setNumberOfDisplayedDataRows(currentRowCount);

        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData, sortedData]);



    // Update paginated data
    const paginatedTableData = sortedData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    //console.log("sortedData: ", sortedData.length);

    // Update total number of pages based on the amount of data and number of rows per page selected
    const totalPages = Math.ceil(sortedData.length / rowsPerPage);

    // Keep the current page within the available page range
    useEffect(() => {

        // An empty table should remain on page one internally
        if (totalPages < 1) {

            if (currentPage !== 1) {
                setCurrentPage(1);
            }

            return;

        }

        // Move back to the final valid page if filtering or deletion removed later pages
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }

    }, [currentPage, setCurrentPage, totalPages]);

    // This stores the columns currently visible after incl/excl tax column switching
    const visibleColumns = columns.filter((col) => {

        // Hide include-tax columns when tax display is off
        if (col.showTax && !showTax) {

            return false;

        }

        // Hide exclude-tax columns when tax display is on
        if (col.showTax === false && showTax) {

            return false;

        }

        // Keep this column
        return true;

    });



    // This tracks whether the table has the text filter expand column
    const tableHasTextFilterColumn = visibleColumns.some((item) => item.textFilter === true);



    // This stores the text filter column width
    const textFilterColumnWidth = tableHasTextFilterColumn ? 24 : 0;



    // This totals the minimum width needed by the table
    const totalMinimumTableWidth = visibleColumns.reduce((totalWidth, col) => {

        // Add this column's minimum width
        return totalWidth + Number(col.minWidth || 150);

    }, textFilterColumnWidth);



    // This calculates final pixel widths for every visible column, respecting minWidth and maxWidth
    const calculatedColumnWidths = useMemo(() => {

        // Store calculated column widths
        const widths = {};

        // Store fixed width total
        let fixedWidthTotal = textFilterColumnWidth;

        // Store flexible columns to calculate later
        const flexibleColumnWork = [];

        // Loop through visible columns
        visibleColumns.forEach((col) => {

            // Read minimum and maximum widths
            const minimumColumnWidth = Number(col.minWidth || 150);
            const maximumColumnWidth = Number(col.maxWidth || 9999);

            // Read manual width if the user has resized this column
            const manualColumnWidth = columnWidths[col.key];

            // Fixed columns and manually resized columns get a fixed calculated width
            if (col.resizable === false || manualColumnWidth) {

                // Work out the raw width
                const rawColumnWidth = Number(manualColumnWidth || col.minWidth || col.maxWidth || 150);

                // Clamp it between min and max
                const calculatedColumnWidth = Math.max(minimumColumnWidth, Math.min(rawColumnWidth, maximumColumnWidth));

                // Store this width
                widths[col.key] = calculatedColumnWidth;

                // Add it to fixed total
                fixedWidthTotal += calculatedColumnWidth;

                return;

            }

            // Flexible columns start at minWidth and can grow up to maxWidth
            flexibleColumnWork.push({
                key: col.key,
                minWidth: minimumColumnWidth,
                maxWidth: maximumColumnWidth,
                width: minimumColumnWidth,
            });

        });

        // Calculate how much width is available to the table, allowing for border and browser rounding
        const availableTableWidth = Math.max(Number(tableContainerWidth || 0) - 2, totalMinimumTableWidth);

        // Calculate how much extra width is available for flexible columns
        let remainingFlexibleWidth = availableTableWidth - fixedWidthTotal - flexibleColumnWork.reduce((totalWidth, column) => {
            return totalWidth + column.minWidth;
        }, 0);

        // Do not allow negative spare width
        remainingFlexibleWidth = Math.max(0, remainingFlexibleWidth);

        // Keep distributing spare width until every column is capped or there is no width left
        let activeFlexibleColumns = flexibleColumnWork.filter((column) => column.maxWidth > column.width);

        while (remainingFlexibleWidth > 0.5 && activeFlexibleColumns.length > 0) {

            // Total the active flexible columns by their minimum widths
            const activeFlexibleWidthTotal = activeFlexibleColumns.reduce((totalWidth, column) => {
                return totalWidth + column.minWidth;
            }, 0);

            // Stop if nothing useful can be calculated
            if (activeFlexibleWidthTotal <= 0) { break; }

            // Track how much width was used in this pass
            let usedFlexibleWidth = 0;

            // Track columns that can still grow after this pass
            const nextActiveFlexibleColumns = [];

            // Give each active flexible column its proportional share
            for (const column of activeFlexibleColumns) {

                // Calculate this column's proportional share
                const proportionalShare = remainingFlexibleWidth * (column.minWidth / activeFlexibleWidthTotal);

                // Calculate how much room this column has left before maxWidth
                const remainingColumnRoom = column.maxWidth - column.width;

                // Add either the proportional share or the remaining room, whichever is smaller
                const widthToAdd = Math.min(proportionalShare, remainingColumnRoom);

                // Grow this column
                column.width += widthToAdd;

                // Track used width
                usedFlexibleWidth += widthToAdd;

                // Keep this column active if it still has room
                if ((column.maxWidth - column.width) > 0.5) {

                    nextActiveFlexibleColumns.push(column);

                }

            }

            // Stop if no useful width was used
            if (usedFlexibleWidth <= 0.5) { break; }

            // Reduce remaining width
            remainingFlexibleWidth -= usedFlexibleWidth;

            // Continue with columns that still have room
            activeFlexibleColumns = nextActiveFlexibleColumns;

        }

        // Store calculated flexible widths
        flexibleColumnWork.forEach((column) => {

            // Store floored width so rounding does not make the table a pixel wider than its container
            widths[column.key] = Math.floor(column.width);

        });

        // Return calculated widths
        return widths;

    }, [visibleColumns, columnWidths, tableContainerWidth, textFilterColumnWidth, totalMinimumTableWidth]);



    // This totals the final table width after maxWidth caps have been applied
    const calculatedTableWidth = visibleColumns.reduce((totalWidth, col) => {

        // Add the calculated width for this column
        return totalWidth + Number(calculatedColumnWidths[col.key] || col.minWidth || 150);

    }, textFilterColumnWidth);



    // This returns the colgroup width style for a column
    const getColumnGroupStyle = (col) => {

        // Return the calculated pixel width
        return { width: calculatedColumnWidths[col.key] || col.minWidth || 150 };

    };



    return (

        <>

            <div className={styles.table_container} ref={tableTopRef}>

                <table
                    style={{
                        borderCollapse: 'collapse',
                        width: calculatedTableWidth,
                        minWidth: calculatedTableWidth,
                        tableLayout: 'fixed',
                        border: "solid 1px var(--input-border)"
                    }}
                >

                    <colgroup>

                        {tableHasTextFilterColumn &&

                            <col style={{ width: 24 }} />

                        }

                        {visibleColumns.map((col) => (

                            <col
                                key={col.key}
                                style={getColumnGroupStyle(col)}
                            />

                        ))}

                    </colgroup>

                    <thead>

                        <tr>

                            {/* Only show text filter expand button if at least one column has a text filter option */}
                            {columns.some(item => item.textFilter === true) &&

                                <th
                                    title={showTextFilter ? "Hide Text Filters" : "Show Text Filters"}
                                    className={styles.text_filter_icon}
                                    style={{ borderBottom: showTextFilter ? 'var(--table-header-color)' : '1px solid var(--input-border)' }}
                                    onClick={() => setShowTextFilter((prev) => !prev)}
                                >
                                    {showTextFilter ? "-" : "+"}
                                </th>

                            }


                            {columns.map((col, index) => {

                                // If the column needs to show tax but the flag is false then don't show this column OR vice versa
                                if ((col.showTax && !showTax) || (col.showTax === false && showTax)) {

                                    return null;

                                } 

                                return (

                                    <th
                                        key={col.key}
                                            draggable={col.draggable !== false}
                                            onDragStart={() => col.draggable !== false && handleDragStart(index, dragColumnIndex)}
                                            onDragOver={(e) => col.draggable !== false && e.preventDefault()}
                                            onDrop={() => col.draggable !== false && handleDrop(index, dragColumnIndex, columns, setColumns)}
                                            style={{ 
                                                position: 'relative',
                                                width: calculatedColumnWidths[col.key],
                                                minWidth: col.minWidth,
                                                maxWidth: col.maxWidth,
                                                borderBottom: showTextFilter ? 'var(--table-header-color)' : '1px solid var(--input-border)' ,
                                                padding: '8px',
                                                whiteSpace: 'nowrap',
                                        
                                                cursor: col.draggable !== false ? 'move' : 'default',
                                            }}
                                            className={
                                            
                                                col.th_classname ? 
                                            
                                                col.th_classname 
                                                
                                                :

                                                ( "" )

                                            }
                                    >

                                        {col.key === 'select' ? (

                                            <div>
                                                <input
                                                    type="checkbox"
                                                    className="customisable-table_select-checkbox"
                                                    onChange={() =>
                                                        toggleSelectAllRows(paginatedTableData, selectedRows, setSelectedRows)
                                                    }
                                                    checked={
                                                        paginatedTableData.length > 0 &&
                                                        paginatedTableData.every(row => selectedRows.includes(row.id))
                                                    }
                                                />
                                            </div>

                                        ) :

                                            col.key === 'view' ? (

                                                ""

                                            ) :
                                                

                                                (

                                                    <>

                                                        <div
                                                            onClick={() => setSortConfig(prev => ({
                                                                key: col.key,
                                                                direction: prev.key === col.key && prev.direction === 'asc' ? 'desc' : 'asc'
                                                            }))}
                                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                                        >
                                                            {sortConfig.key === col.key ? (sortConfig.direction === 'asc' ? <ArrowDownCollapseIcon /> : <ArrowUpCollapseIcon />) : ' '} {col.label}
                                                        </div>

                                                        {/*  
                                                        <input
                                                            type="text"
                                                            value={textFilters[col.key] || ''}
                                                            onChange={(e) =>
                                                                setTextFilters((prev) => ({ ...prev, [col.key]: e.target.value }))
                                                            }
                                                            placeholder={`${col.label}`}
                                                            style={{ width: '90%', padding: '5px 7px', marginTop: '10px' }}

                                                        />
                                                        */ }

                                                    </>

                                                )}



                                        {col.resizable && (

                                            <div
                                                onMouseDown={(e) => handleResize(e, col.key, calculatedColumnWidths, columns, setColumnWidths)}
                                                className={styles.resize_handle}
                                            />

                                        )}
                                                                    
                                    </th>

                                

                            )})}

                        </tr>

                        {/* Render the text filter row based on showTextFilter state */}
                        <tr style={{ display: showTextFilter ? "" : "none" }}>

                            
                                <th className={styles.text_filter_icon}></th>
                            

                            {/* Render the text filter per coloumn header */}
                            {columns.map((col) => {
                                
                                 // If the column needs to show tax but the flag is false then don't show this column OR vice versa
                                if ((col.showTax && !showTax) || (col.showTax === false && showTax)) {

                                    return null;

                                } 
                                                                
                                return (

                                    <th key={col.key} style={{ padding: '4px', position: 'relative' }}>

                                        {/* Only show text filter input if textFilter is set to true for that column */}
                                        {col.textFilter !== false && (
                                            <input
                                                type="text"
                                                value={textFilters[col.key] || ''}
                                                onChange={(e) =>
                                                    setTextFilters((prev) => ({ ...prev, [col.key]: e.target.value }))
                                                }
                                                placeholder={`${col.label}`}
                                                style={{
                                                    width: '90%', padding: '5px 7px', marginTop: '1px',   
                                                }}
                                            />
                                        )}

                                        {col.resizable && (

                                            <div
                                                onMouseDown={(e) => handleResize(e, col.key, columnWidths, columns, setColumnWidths)}
                                                className={styles.resize_handle}
                                            />

                                        )}

                                    </th>

                                )

                            })}

                        </tr>

                        


                    </thead>


                    <tbody>

                        
                        { loadingData ?

                            <tr>

                                <td colSpan={columns.length}>
                                                                    
                                    Loading<LoadingDotsAnimation />
                                      
                                </td>

                            </tr>

                            :

                            paginatedTableData.length === 0 ?

                                <tr>

                                    <td colSpan={columns.length}>

                                        {noDataAvaiableMessage ? noDataAvaiableMessage : "No data to show" }

                                    </td>

                                </tr>

                            :


                            paginatedTableData.map((row, index) => {

                                // Check if we have a real row click handler
                                const hasRowClickHandler = typeof handleRowSingleClick === "function";

                                // Check if we have a row link builder
                                const hasRowLink = typeof getRowLink === "function";

                                // Pre-calc the URL if we are in "link mode"
                                const rowLink = !hasRowClickHandler && hasRowLink
                                    ? getRowLink(row)
                                    : null;

                                // Decide if the row should be interactive at all
                                const rowIsInteractive = hasRowClickHandler || !!rowLink;

                                // Define a single click handler for the row
                                const handleRowClick = () => {

                                    // If the parent provided a row click handler, use that first
                                    if (hasRowClickHandler) {

                                        // Call the parent handler with the row id
                                        handleRowSingleClick(row.id);

                                        // Return early so we do not navigate as well
                                        return;

                                    }

                                    // If there is no row click handler but we have a link, navigate to it
                                    if (rowLink) {

                                        // Use React Router navigation to go to the desired URL
                                        navigate(rowLink);

                                    }

                                };


                                return (

                                    // Set the key to the row.id if there is one, if not then use the index
                                    <React.Fragment key={`${rowKeyPrefix}-${row.id}` || index}>

                                        {/* Check if the row id matches one in selected - if so apply a colour to highlight it */}

                                        <tr
                                            style={{

                                                backgroundColor: selectedRows?.includes(row.id) ? "var(--primary-color-trans)" : "",
                                                cursor: rowIsInteractive ? "pointer" : undefined
                                            }}

                                            // Only add the onClick handler if there is something to do
                                            onClick={rowIsInteractive ? handleRowClick : undefined}
                                        >

                                            {/* Add an extra td cell if the table has a show text filter expand button (if at least one column has a text filter option) */}
                                            {columns.some(item => item.textFilter === true) &&

                                                <td></td>

                                            }

                                            {columns.map((col) => {

                                                // If the column needs to show tax but the flag is false then don't show this column OR vice versa
                                                if ((col.showTax && !showTax) || (col.showTax === false && showTax)) {

                                                    return null;

                                                } 
                                                
                                                return (

                                                <td key={col.key}

                                                    className={
                                                        
                                                        //col.key === 'transactionAmount' || col.key === 'originalTransactionAmount' ?

                                                        col.key === 'alignToRight' ?

                                                            ( styles.align_text_right )

                                                            :

                                                            col.td_classname ? 
                                                            
                                                                col.td_classname 

                                                                :

                                                                ( "" )

                                                    }
                                                    onClick={(e) => {

                                                        if (col.key === 'select') {

                                                            e.stopPropagation();
                                                            toggleRowSelection(row.id, setSelectedRows);

                                                        }
                                                       
                                                    }}
                                                >

                                                    {col.key === 'select' ?

                                                        (
                                                            <input
                                                                type="checkbox"
                                                                className="customisable-table_select-checkbox"
                                                                checked={selectedRows.includes(row.id)}

                                                                // Prevent row click
                                                                onClick={(e) => e.stopPropagation()}

                                                                // Toggle row selection
                                                                onChange={() => toggleRowSelection(row.id, setSelectedRows) }
                                                            />
                                                        )

                                                        :

                                                        col.key === 'view' ?

                                                            (
                                                                // Display context menu button - disable if any rows are selected as it might be confusing to select one and then action a menu item on another
                                                                <ContextMenuButton buttonTitle="&hellip;" menuOptions={menuOptions} selectedIDs={row.id} selectedDetails={row} disabled={selectedRows.length > 0} itemIsActive={row.active} />
                                                                                                                        
                                                            )

                                                            :

                                                        col.key === 'orderItemFirst' && row.expandRow ?

                                                            (

                                                                <span
                                                                        style={{ cursor: 'pointer' }}
                                                                        onClick={(e) => {

                                                                            // Prevent row click
                                                                            e.stopPropagation();

                                                                            // Toggle row expansion
                                                                            toggleRowExpansion(row.id, setExpandedRows);
                                                                        }}
                                                                >
                                                                    {expandedRows.includes(row.id) ? '^' : '+'} {row[col.key]}

                                                                    {/* Inline expansion of remaining order items */}
                                                                        {expandedRows.includes(row.id) &&

                                                                            row.orderItemRemaining.map((item, index) => (

                                                                                <span key={`remaining-${row.id}-${index}`}>

                                                                                    <br />

                                                                                    <span style={{ marginLeft: '10px' }}>{item}</span>

                                                                                </span>

                                                                        ))}

                                                                </span>

                                                            )

                                                            :

                                                            col.key === 'orderItemFirst' && !row.expandRow ?

                                                                (

                                                                    <span style={{ marginLeft: '10px' }}> {row[col.key]} </span>

                                                                )

                                                                    :

                                                                    col.key === 'transactionAmount' ? (

                                                                        // Render with conditional styling based on whether the value starts with '-'
                                                                        <span className={
                                                                            styles.currency_amount + ' ' +
                                                                            (String(row[col.key]).trim().startsWith('-') ? styles.negative : styles.positive)
                                                                        } >
                                                                            {row[col.key]}
                                                                        </span>
                                                                    )

                                                                        :

                                                                        col.key === 'creationdate' || col.key === 'transactionDate' ? (

                                                                            // Render only if it's not null and format it nicely
                                                                            <span>
                                                                                {CheckNullDate(row[col.key]) === null ? "" : formatDisplayDateTime(row[col.key])}
                                                                            </span>

                                                                        )

                                                                            :


                                                                            // Check if the column 'type' is image so we can display the image url data in <img> tag
                                                                            col.type === "image" ? (

                                                                                // Could put something in here for selected image size s,m,l or allow custom size to be sent in with initial data

                                                                                <img className={styles.image_cell} src={row[col.key]} alt={row[col.imageNameKey]} />

                                                                            )

                                                                            :

                                                                                col.key === 'active' && activeTogglefunction ?

                                                                                    (
                                                                                        // Display toggle for setting active state of the table row data
                                                                                        <div onClick={(e) =>  e.stopPropagation() }  > 
                                                                                            <ToggleSwitch 
                                                                                                size="s" 
                                                                                                value={row.active === "Y" ? true : false} 
                                                                                                onChange={(e) => {   
                                                                                                    activeTogglefunction(row.id, e.target.value)
                                                                                                }} 
                                                                                            />                 
                                                                                        </div>
                                                                                    )

                                                                                    : 

                                                                                        col.key === 'deliverycountry' ?

                                                                                        (
                                                                                            // Display the country with its API-supplied flag
                                                                                            renderCountryDestination(row[col.key])
                                                                                        )

                                                                                        :
                                                                                
                                                            (

                                                                <span> {row[col.key]} </span>

                                                            )}

                                                </td>

                                            )})}

                                        </tr>

                                    </React.Fragment>

                            )})}

                    </tbody>

                </table>

            </div>


            <div>
                <Pagination
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    totalPages={totalPages}
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={setRowsPerPage}
                    rowsPerPageOptions={[10, 50, 100, 500]}
                    setSelectedRows={setSelectedRows}
                    paginationDropdownMessage="Rows"
                    onPageChangeScroll={handleScrollToTableTop}
                />
            </div>

        </>

    );

};

export default CustomisableTable;
